/**
 * MKS-351 — the single source of truth for how the SPA renders and parses a
 * calendar date.
 *
 * Before this file the app formatted dates in three different ways at once:
 * `toLocaleDateString('cs-CZ')` (`20. 11. 2026`, with spaces), plain
 * `toLocaleDateString('en-US')` (`11/20/2026`), and native
 * `<input type="date">` controls, which render in the *browser's* locale and
 * so put `11/20/2026` next to `20. 11. 2026` on the same invoice page.
 *
 * Everything now goes through the {@link DateFormatSpec} returned by
 * {@link getActiveDateFormat}. That is deliberately ONE mutable module-level
 * value rather than a constant: MKS-314 adds an instance locale setting, and
 * all it has to do is call {@link setActiveDateFormat} with a different spec
 * (US `mm/dd/yyyy`, ISO `yyyy-mm-dd`, …). No call site changes.
 *
 * Month and weekday NAMES are part of the spec too, not module constants. The
 * ticket asks for English labels (`October 2026`), but the flag's OFF branch
 * has to keep rendering what production renders today (`říjen 2026`), so the
 * names have to swap with everything else. A surface that was already English
 * before MKS-351 passes {@link ENGLISH_LABEL_FORMAT} and reads the same in both
 * branches.
 */

/** Which order the three date parts are written in. */
export type DateFieldOrder = 'dmy' | 'mdy' | 'ymd';

export interface DateFormatSpec {
  order: DateFieldOrder;
  /** The character between the parts (`.` for `dd.mm.yyyy`, `/` for `mm/dd/yyyy`). */
  separator: string;
  /** Render a space after every separator — the Czech convention, `20. 11. 2026`. */
  spaced: boolean;
  /** Zero-pad day and month to two digits — `07.05.1978` rather than `7.5.1978`. */
  padded: boolean;
  /**
   * How a time of day is written — `14:30` or `2:30 PM`.
   *
   * It lives on the date spec because it is the same decision: a US instance
   * that writes `10/01/2026` writes `2:30 PM` next to it. Built by hand in
   * {@link formatDateTime} rather than through `toLocaleTimeString`, which is
   * what the CI guard forbids and what MKS-351 exists to remove.
   */
  clock: '24h' | '12h';
  /** Month names, January first. */
  monthNames: readonly string[];
  /** Abbreviated month names, January first. */
  monthNamesShort: readonly string[];
  /** Weekday names, MONDAY first (the European week). */
  weekdayNames: readonly string[];
  /** Abbreviated weekday names, MONDAY first. */
  weekdayNamesShort: readonly string[];
}

/**
 * Build a name table by asking `Intl` once, at module load.
 *
 * Only the legacy spec below uses this — the European spec carries the English
 * names as plain constants. Deriving the Czech tables rather than hand-writing
 * them is what makes the release flag's OFF branch render the same strings
 * production renders today, including whatever abbreviation ICU picks, instead
 * of a hand-typed list that drifts from it.
 */
function buildNameTable(
  locale: string,
  kind: 'month' | 'weekday',
  width: 'long' | 'short'
): readonly string[] {
  const formatter = new Intl.DateTimeFormat(locale, { [kind]: width, timeZone: 'UTC' });
  if (kind === 'month') {
    // The 15th avoids any month-length edge; the year is arbitrary.
    return Array.from({ length: 12 }, (_, i) => formatter.format(Date.UTC(2021, i, 15)));
  }
  // 2021-11-01 was a Monday, so this walks Monday → Sunday.
  return Array.from({ length: 7 }, (_, i) => formatter.format(Date.UTC(2021, 10, 1 + i)));
}

/** English month names — `October 2026`, never the browser's locale (MKS-351). */
export const MONTH_NAMES_EN = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** English three-letter month abbreviations, for chart axes and dense tables. */
export const MONTH_NAMES_SHORT_EN = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** English weekday abbreviations, Monday first (the European week). */
export const WEEKDAY_NAMES_SHORT_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** English weekday names, Monday first, matching {@link WEEKDAY_NAMES_SHORT_EN}. */
export const WEEKDAY_NAMES_EN = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

/**
 * MKS-351's target: `dd.mm.yyyy`, no spaces, both parts zero-padded, English
 * month and weekday names.
 */
export const EUROPEAN_DATE_FORMAT: DateFormatSpec = {
  order: 'dmy',
  separator: '.',
  spaced: false, // `20.11.2026` — unlike the Czech convention below
  padded: true,
  clock: '24h',
  monthNames: MONTH_NAMES_EN,
  monthNamesShort: MONTH_NAMES_SHORT_EN,
  weekdayNames: WEEKDAY_NAMES_EN,
  weekdayNamesShort: WEEKDAY_NAMES_SHORT_EN,
};

/**
 * The German option: `01.10.2026`, identical to {@link EUROPEAN_DATE_FORMAT}
 * in every DATE field.
 *
 * It is a separate constant rather than an alias because the difference
 * between the two options is real but lives elsewhere — German writes
 * `1.234,50` where European writes `1 234,50`, and that comes from
 * `utils/localeFormat.ts` and the instance locale tag, not from this spec.
 * Keeping the option distinct here is what lets the admin control show one row
 * per option and what keeps the mapping in {@link LOCALE_DATE_FORMAT_OPTION}
 * total.
 */
export const GERMAN_DATE_FORMAT: DateFormatSpec = {
  order: 'dmy',
  separator: '.',
  spaced: false,
  padded: true,
  clock: '24h',
  monthNames: MONTH_NAMES_EN,
  monthNamesShort: MONTH_NAMES_SHORT_EN,
  weekdayNames: WEEKDAY_NAMES_EN,
  weekdayNamesShort: WEEKDAY_NAMES_SHORT_EN,
};

/** The UK option: `01/10/2026`, day first with slashes, 24-hour. */
export const UK_DATE_FORMAT: DateFormatSpec = {
  order: 'dmy',
  separator: '/',
  spaced: false,
  padded: true,
  clock: '24h',
  monthNames: MONTH_NAMES_EN,
  monthNamesShort: MONTH_NAMES_SHORT_EN,
  weekdayNames: WEEKDAY_NAMES_EN,
  weekdayNamesShort: WEEKDAY_NAMES_SHORT_EN,
};

/** The US option: `10/01/2026`, month first, and the only 12-hour clock. */
export const US_DATE_FORMAT: DateFormatSpec = {
  order: 'mdy',
  separator: '/',
  spaced: false,
  padded: true,
  clock: '12h',
  monthNames: MONTH_NAMES_EN,
  monthNamesShort: MONTH_NAMES_SHORT_EN,
  weekdayNames: WEEKDAY_NAMES_EN,
  weekdayNamesShort: WEEKDAY_NAMES_SHORT_EN,
};

/**
 * A spec whose month and weekday NAMES are always English, whatever the
 * release flag says.
 *
 * It exists for the three surfaces that already rendered an English month
 * before MKS-351 — the month grid heading, the revenue chart axis and the My
 * Tasks due cell all called `toLocaleDateString('en-US' | 'en-GB', …)`. Those
 * must read English in BOTH flag branches: the flag is not allowed to change
 * them at all, and routing them through the legacy Czech spec would have
 * turned `October 2026` into `říjen 2026` in production.
 *
 * Only the name tables are read by the month/weekday helpers these surfaces
 * call, so the date-shape fields it inherits never reach the screen.
 */
export const ENGLISH_LABEL_FORMAT: DateFormatSpec = EUROPEAN_DATE_FORMAT;

/**
 * What `toLocaleDateString('cs-CZ')` produced before MKS-351: `20. 11. 2026`,
 * unpadded with a space after each dot, and Czech month and weekday names.
 *
 * This is the release flag's OFF branch. It exists so production keeps
 * rendering today's dates — same order, same spacing, same language — until
 * somebody verifies the ticket. The flag's cleanup task deletes this constant
 * and the branch that selects it.
 */
export const LEGACY_CZ_DATE_FORMAT: DateFormatSpec = {
  order: 'dmy',
  separator: '.',
  spaced: true,
  padded: false,
  clock: '24h',
  monthNames: buildNameTable('cs-CZ', 'month', 'long'),
  monthNamesShort: buildNameTable('cs-CZ', 'month', 'short'),
  weekdayNames: buildNameTable('cs-CZ', 'weekday', 'long'),
  weekdayNamesShort: buildNameTable('cs-CZ', 'weekday', 'short'),
};

/**
 * The four date formats an instance may be written in. Not locales: a format
 * option is a shape (`dd.mm.yyyy`), and several locales map onto the same one.
 */
export type DateFormatOption = 'european' | 'german' | 'uk' | 'us';

/** Every option, by key. The one place a spec is looked up by option. */
export const DATE_FORMAT_OPTIONS: Record<DateFormatOption, DateFormatSpec> = {
  european: EUROPEAN_DATE_FORMAT,
  german: GERMAN_DATE_FORMAT,
  uk: UK_DATE_FORMAT,
  us: US_DATE_FORMAT,
};

/** How each option is named in the admin control. */
export const DATE_FORMAT_OPTION_LABELS: Record<DateFormatOption, string> = {
  european: 'European',
  german: 'German',
  uk: 'UK',
  us: 'US',
};

/** The option a brand-new instance, and any unknown locale, is written in. */
export const DEFAULT_DATE_FORMAT_OPTION: DateFormatOption = 'european';

/**
 * MKS-351 follow-up — locale → format option, the ONE place that mapping
 * exists.
 *
 * This replaces reading the shape out of `Intl`. Probing ICU was the defect
 * Petra found on staging: an instance set to `cs-CZ` — the default, and
 * TopMonks' own — got ICU's Czech conventions back, so the app rendered
 * `1. 10. 2026` (spaced, unpadded) with `říjen` for the month, which is
 * exactly what this ticket set out to remove. The formats below are a product
 * decision, not a property of the locale, so they are written down rather than
 * derived; month and weekday names are English in every option, because the UI
 * language is English whatever the format.
 *
 * `sk-SK` maps to European for the same reason `cs-CZ` does: the locale picks
 * the number separators and the collation, and the date shape the company
 * asked for is `dd.mm.yyyy`.
 */
export const LOCALE_DATE_FORMAT_OPTION: Record<string, DateFormatOption> = {
  'cs-CZ': 'european',
  'sk-SK': 'european',
  'de-DE': 'german',
  'en-GB': 'uk',
  'en-US': 'us',
};

/** The option a locale is written in — European for anything unrecognised. */
export function dateFormatOptionForLocale(locale: string): DateFormatOption {
  return LOCALE_DATE_FORMAT_OPTION[locale] ?? DEFAULT_DATE_FORMAT_OPTION;
}

/**
 * MKS-314 — the {@link DateFormatSpec} an instance locale is written in.
 *
 * Returns one of the four shared option constants, never a fresh object, so
 * callers may compare specs by identity.
 */
export function dateFormatForLocale(locale: string): DateFormatSpec {
  return DATE_FORMAT_OPTIONS[dateFormatOptionForLocale(locale)];
}

/**
 * The format in force right now. Starts on the legacy spec because the release
 * flag is fail-closed: a surface rendered before `useDateFormatSync` has run
 * (a component test that mounts a page on its own, a render before the flags
 * query resolves) must show today's production output, not the new one.
 */
let activeDateFormat: DateFormatSpec = LEGACY_CZ_DATE_FORMAT;

export function getActiveDateFormat(): DateFormatSpec {
  return activeDateFormat;
}

/**
 * Swap the app-wide date format. Called from `useDateFormatSync` (the one
 * place that reads the MKS-351 release flag) and, later, by MKS-314's locale
 * setting. Tests call it directly to exercise a specific spec.
 */
export function setActiveDateFormat(spec: DateFormatSpec): void {
  activeDateFormat = spec;
}

/** What every formatter in this file returns for missing or unparseable input. */
export const DATE_PLACEHOLDER = '-';

/** A calendar date with no time and no timezone. */
export interface DateParts {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
}

/** Is `{year, month, day}` a real calendar day (rejects 31.02., month 13, …)? */
function isRealDate({ year, month, day }: DateParts): boolean {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false;
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > 31) return false;
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day
  );
}

/**
 * Pull the calendar date out of whatever the API handed us.
 *
 * A `YYYY-MM-DD` (or a full ISO timestamp) is split by hand rather than fed to
 * `new Date(...)`: Prisma `@db.Date` columns arrive as midnight-UTC strings, so
 * `new Date(iso).getDate()` rolls back a day in every timezone behind UTC. A
 * `Date` object is read in LOCAL time, because a `Date` in this app is a real
 * instant (`createdAt`) and the viewer expects their own day.
 */
export function toDateParts(value: string | Date | null | undefined): DateParts | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return { year: value.getFullYear(), month: value.getMonth() + 1, day: value.getDate() };
  }
  const datePart = value.split('T')[0] ?? '';
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(datePart);
  if (!m) return null;
  const parts = { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
  return isRealDate(parts) ? parts : null;
}

/** Render `{year, month, day}` through `spec`. Pure — no `Intl`, no locale. */
export function formatDateParts(
  parts: DateParts,
  spec: DateFormatSpec = getActiveDateFormat()
): string {
  const dd = spec.padded ? String(parts.day).padStart(2, '0') : String(parts.day);
  const mm = spec.padded ? String(parts.month).padStart(2, '0') : String(parts.month);
  const yyyy = String(parts.year).padStart(4, '0');
  const sep = spec.spaced ? `${spec.separator} ` : spec.separator;
  const ordered =
    spec.order === 'mdy' ? [mm, dd, yyyy] : spec.order === 'ymd' ? [yyyy, mm, dd] : [dd, mm, yyyy];
  return ordered.join(sep);
}

/**
 * Format a calendar date for display — `20.11.2026` under the European spec.
 * Accepts `YYYY-MM-DD`, a full ISO timestamp, or a `Date`. Never throws;
 * returns {@link DATE_PLACEHOLDER} for anything it cannot read.
 */
export function formatDate(
  value: string | Date | null | undefined,
  spec: DateFormatSpec = getActiveDateFormat()
): string {
  const parts = toDateParts(value);
  return parts ? formatDateParts(parts, spec) : DATE_PLACEHOLDER;
}

/**
 * Render the clock part of an instant through `spec.clock` — `14:30`, or
 * `2:30 PM` under the US option.
 *
 * Built from the hour and minute by hand. `toLocaleTimeString` is the call the
 * CI guard bans and the one this ticket exists to remove: it would read the
 * BROWSER's locale, which is how a US laptop ended up putting `2:30 PM` on a
 * Czech company's page in the first place.
 */
function formatClock(date: Date, spec: DateFormatSpec): string {
  const mi = String(date.getMinutes()).padStart(2, '0');
  const hours = date.getHours();
  if (spec.clock === '12h') {
    // 0 and 12 both read as `12`; the suffix is what tells them apart.
    const h12 = hours % 12 === 0 ? 12 : hours % 12;
    return `${h12}:${mi} ${hours < 12 ? 'AM' : 'PM'}`;
  }
  return `${String(hours).padStart(2, '0')}:${mi}`;
}

/**
 * Format a real instant for display — `20.11.2026 14:30`, in the viewer's
 * local timezone and the active spec's clock. Never throws.
 */
export function formatDateTime(
  value: string | Date | null | undefined,
  spec: DateFormatSpec = getActiveDateFormat()
): string {
  if (value == null || value === '') return DATE_PLACEHOLDER;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return DATE_PLACEHOLDER;
  const parts: DateParts = {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
  return `${formatDateParts(parts, spec)} ${formatClock(date, spec)}`;
}

/** Just the clock part of an instant — `14:30`, or `2:30 PM` under the US option. */
export function formatTime(
  value: string | Date | null | undefined,
  spec: DateFormatSpec = getActiveDateFormat()
): string {
  if (value == null || value === '') return DATE_PLACEHOLDER;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return DATE_PLACEHOLDER;
  return formatClock(date, spec);
}

/**
 * `October 2026` (`říjen 2026` while the release flag is off). Accepts the same
 * inputs as {@link formatDate} plus a bare `YYYY-MM` period string, which is
 * what most of the invoicing surfaces hold.
 */
export function formatMonthYear(
  value: string | Date | null | undefined,
  spec: DateFormatSpec = getActiveDateFormat()
): string {
  const parts = toMonthParts(value);
  if (!parts) return DATE_PLACEHOLDER;
  return `${spec.monthNames[parts.month - 1]} ${parts.year}`;
}

/** `Oct 2026` — the dense variant of {@link formatMonthYear}. */
export function formatMonthYearShort(
  value: string | Date | null | undefined,
  spec: DateFormatSpec = getActiveDateFormat()
): string {
  const parts = toMonthParts(value);
  if (!parts) return DATE_PLACEHOLDER;
  return `${spec.monthNamesShort[parts.month - 1]} ${parts.year}`;
}

/** `Oct 26` — for chart axes, where a four-digit year does not fit. */
export function formatMonthYear2Digit(
  value: string | Date | null | undefined,
  spec: DateFormatSpec = getActiveDateFormat()
): string {
  const parts = toMonthParts(value);
  if (!parts) return DATE_PLACEHOLDER;
  return `${spec.monthNamesShort[parts.month - 1]} ${String(parts.year).slice(-2)}`;
}

/** `Oct` alone. */
export function formatMonthShort(
  value: string | Date | null | undefined,
  spec: DateFormatSpec = getActiveDateFormat()
): string {
  const parts = toMonthParts(value);
  if (!parts) return DATE_PLACEHOLDER;
  return spec.monthNamesShort[parts.month - 1] ?? DATE_PLACEHOLDER;
}

/**
 * `Mon` — the abbreviated weekday of a calendar date. Used by the holidays
 * card, where the weekday is the whole point of the row.
 */
export function formatWeekdayShort(
  value: string | Date | null | undefined,
  spec: DateFormatSpec = getActiveDateFormat()
): string {
  const index = weekdayIndex(value);
  return index === null ? DATE_PLACEHOLDER : (spec.weekdayNamesShort[index] ?? DATE_PLACEHOLDER);
}

/** `Monday` — the spelled-out weekday of a calendar date. */
export function formatWeekdayLong(
  value: string | Date | null | undefined,
  spec: DateFormatSpec = getActiveDateFormat()
): string {
  const index = weekdayIndex(value);
  return index === null ? DATE_PLACEHOLDER : (spec.weekdayNames[index] ?? DATE_PLACEHOLDER);
}

/** Monday-first weekday index (0 = Monday) of a calendar date, or null. */
function weekdayIndex(value: string | Date | null | undefined): number | null {
  const parts = toDateParts(value);
  if (!parts) return null;
  const jsDay = new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay();
  return (jsDay + 6) % 7;
}

/**
 * `20 Nov` — day and month with no year, for dense rows (My Tasks) and chart
 * axes. The month name comes from the spec, never from the browser locale.
 */
export function formatDayMonthShort(
  value: string | Date | null | undefined,
  spec: DateFormatSpec = getActiveDateFormat()
): string {
  const parts = toDateParts(value);
  if (!parts) return DATE_PLACEHOLDER;
  return `${parts.day} ${spec.monthNamesShort[parts.month - 1]}`;
}

/**
 * `21 October` — day and spelled-out month, no year. Used by the time-off
 * coverage warning, which lists a run of days under one month name.
 */
export function formatDayMonthLong(
  value: string | Date | null | undefined,
  spec: DateFormatSpec = getActiveDateFormat()
): string {
  const parts = toDateParts(value);
  if (!parts) return DATE_PLACEHOLDER;
  return `${parts.day} ${spec.monthNames[parts.month - 1]}`;
}

/**
 * `20.11` — day and month in the active order, no year. Used by the week/day
 * navigation strips, where the year is already in the heading.
 */
export function formatDayMonth(
  value: string | Date | null | undefined,
  spec: DateFormatSpec = getActiveDateFormat()
): string {
  const parts = toDateParts(value);
  if (!parts) return DATE_PLACEHOLDER;
  const dd = spec.padded ? String(parts.day).padStart(2, '0') : String(parts.day);
  const mm = spec.padded ? String(parts.month).padStart(2, '0') : String(parts.month);
  const sep = spec.spaced ? `${spec.separator} ` : spec.separator;
  const ordered = spec.order === 'mdy' ? [mm, dd] : [dd, mm];
  // Trailing separator too: `1. 5.` is what cs-CZ renders for a year-less date,
  // and `20.11.` is its European equivalent.
  return `${ordered.join(sep)}${spec.separator}`;
}

/**
 * Month+year out of a `YYYY-MM` period, a `YYYY-MM-DD`, an ISO timestamp or a
 * `Date`. A `YYYY-MM` is the one shape {@link toDateParts} rejects, because it
 * has no day.
 */
function toMonthParts(value: string | Date | null | undefined): { year: number; month: number } | null {
  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{1,2})$/.exec(value.trim());
    if (m) {
      const year = Number(m[1]);
      const month = Number(m[2]);
      return month >= 1 && month <= 12 ? { year, month } : null;
    }
  }
  const parts = toDateParts(value);
  return parts ? { year: parts.year, month: parts.month } : null;
}

/**
 * Render an ISO `YYYY-MM-DD` as the text a {@link DateFormatSpec} input shows.
 * Unlike {@link formatDate} it returns `''` (not `-`) for empty input, because
 * the result goes straight into an `<input value={...}>`.
 */
export function toDateInputText(
  value: string | null | undefined,
  spec: DateFormatSpec = getActiveDateFormat()
): string {
  const parts = toDateParts(value);
  return parts ? formatDateParts(parts, spec) : '';
}

/**
 * Parse what a person typed into a date field back to ISO `YYYY-MM-DD`, or
 * `null` when it is not a real date yet.
 *
 * Accepts, in this order:
 *  - ISO `YYYY-MM-DD` verbatim. It is unambiguous in every spec, it is what
 *    the rest of the app speaks, and accepting it is what lets a test or a
 *    paste set the field without knowing the display format.
 *  - three groups separated by any run of non-digits (`20.11.2026`,
 *    `20/11/2026`, `20 11 2026`), mapped by `spec.order`.
 *  - six or eight bare digits (`20112026`, `201126`), mapped by `spec.order` —
 *    typing a date without reaching for the separator key.
 *
 * A two-digit year resolves to 2000-2069 / 1970-1999, the window
 * `Date.parse`'s own two-digit handling uses.
 */
export function parseDateInput(
  text: string,
  spec: DateFormatSpec = getActiveDateFormat()
): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);
  if (iso) {
    const parts = { year: Number(iso[1]), month: Number(iso[2]), day: Number(iso[3]) };
    return isRealDate(parts) ? toIsoDate(parts) : null;
  }

  const groups = splitDateGroups(trimmed);
  if (!groups) return null;

  const [a, b, c] = groups;
  const raw =
    spec.order === 'mdy'
      ? { month: a, day: b, year: c }
      : spec.order === 'ymd'
        ? { year: a, month: b, day: c }
        : { day: a, month: b, year: c };

  const parts = {
    year: expandYear(raw.year),
    month: Number(raw.month),
    day: Number(raw.day),
  };
  return isRealDate(parts) ? toIsoDate(parts) : null;
}

/**
 * Split typed text into exactly three numeric groups. Returns the groups as
 * written (still strings, so a two-digit year is still distinguishable from a
 * four-digit one), or null when the shape is not three parts.
 */
function splitDateGroups(text: string): [string, string, string] | null {
  const digitsOnly = /^\d+$/.test(text);
  if (digitsOnly) {
    // ddmmyyyy / ddmmyy — the year is whatever is left after two 2-digit parts.
    if (text.length === 8) return [text.slice(0, 2), text.slice(2, 4), text.slice(4)];
    if (text.length === 6) return [text.slice(0, 2), text.slice(2, 4), text.slice(4)];
    return null;
  }
  const groups = text.split(/\D+/).filter((g) => g.length > 0);
  if (groups.length !== 3) return null;
  return [groups[0]!, groups[1]!, groups[2]!];
}

/** `26` -> 2026, `78` -> 1978, `2026` -> 2026. */
function expandYear(raw: string): number {
  const n = Number(raw);
  if (raw.length > 2) return n;
  return n < 70 ? 2000 + n : 1900 + n;
}

/** `{2026, 11, 20}` -> `'2026-11-20'`. */
export function toIsoDate(parts: DateParts): string {
  return `${String(parts.year).padStart(4, '0')}-${String(parts.month).padStart(2, '0')}-${String(
    parts.day
  ).padStart(2, '0')}`;
}

/** Today as ISO `YYYY-MM-DD` in the viewer's own timezone. */
export function todayIso(): string {
  const now = new Date();
  return toIsoDate({ year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() });
}

/** The placeholder text a date input shows — `dd.mm.yyyy` for the active spec. */
export function dateInputPlaceholder(spec: DateFormatSpec = getActiveDateFormat()): string {
  const sep = spec.spaced ? `${spec.separator} ` : spec.separator;
  const ordered =
    spec.order === 'mdy'
      ? ['mm', 'dd', 'yyyy']
      : spec.order === 'ymd'
        ? ['yyyy', 'mm', 'dd']
        : ['dd', 'mm', 'yyyy'];
  return ordered.join(sep);
}
